import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, TrendingDown, DollarSign, BarChart2 } from 'lucide-react';

export default function RiskDisclosure() {
  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white mb-6 transition-colors text-sm">
          <ArrowLeft size={14} /><span className="font-body">Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Risk Disclosure</h1>
              <p className="font-body text-white/40 text-sm">Important information for traders</p>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#F59E0B]/20 to-[#EF4444]/20 border border-[#F59E0B]/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <p className="font-body text-[#F59E0B] text-sm leading-relaxed font-medium">
                Trading in foreign exchange (Forex), commodities, and other financial instruments carries a high level of risk and may not be suitable for all investors. Before deciding to trade, you should carefully consider your investment objectives, level of experience, and risk appetite.
              </p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 md:p-8 space-y-6">
            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">1. High Risk Investment</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Trading leveraged products such as Forex and CFDs involves substantial risk of loss and may not be suitable for all investors. The high degree of leverage can work against you as well as for you. You could sustain a loss of some or all of your initial investment.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">2. No Guarantee of Profits</h2>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div className="p-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/20 text-center">
                  <TrendingDown className="w-6 h-6 text-[#EF4444] mx-auto mb-2" />
                  <div className="font-heading font-semibold text-white text-sm">Losses Possible</div>
                  <div className="font-body text-xs text-white/40 mt-1">You may lose capital</div>
                </div>
                <div className="p-4 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-center">
                  <BarChart2 className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
                  <div className="font-heading font-semibold text-white text-sm">Past ≠ Future</div>
                  <div className="font-body text-xs text-white/40 mt-1">Results may vary</div>
                </div>
                <div className="p-4 rounded-xl bg-[#007AFF]/10 border border-[#007AFF]/20 text-center">
                  <DollarSign className="w-6 h-6 text-[#007AFF] mx-auto mb-2" />
                  <div className="font-heading font-semibold text-white text-sm">Risk Capital</div>
                  <div className="font-body text-xs text-white/40 mt-1">Only trade what you can lose</div>
                </div>
              </div>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                There is no guarantee that any trading system, including the Monk Breakout EA, will generate profits or prevent losses. Past performance shown in backtests or historical data is not indicative of future results.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">3. Automated Trading Risks</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-3">
                Using automated trading systems (Expert Advisors) carries additional risks:
              </p>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Technical failures including internet connectivity issues, server downtime, and platform errors
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Slippage and requotes during volatile market conditions
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Differences between backtest results and live trading due to market conditions
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Broker-specific execution policies and spread variations
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">4. Backtest Limitations</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                The performance data shown on this website is based on historical backtesting. Backtest results have inherent limitations: they are prepared with the benefit of hindsight, do not reflect actual trading, and may not account for all market factors such as liquidity constraints and slippage.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">5. Not Financial Advice</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Nothing on this website constitutes investment advice, financial advice, trading advice, or any other sort of advice. You should not treat any of the website's content as such. Monk Algo does not recommend that any financial instrument should be bought, sold, or held by you.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">6. Your Responsibilities</h2>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Conduct your own due diligence before trading
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Consult with a qualified financial advisor if needed
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Only invest money that you can afford to lose
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Understand the risks associated with leveraged trading
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Monitor your trading account regularly
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">7. Disclaimer of Liability</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Monk Algo, its owners, employees, and affiliates will not accept liability for any loss or damage, including without limitation any loss of profit, which may arise directly or indirectly from the use of or reliance on the information provided on this website or the use of our EA software.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">8. Acknowledgment</h2>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="font-body text-white/70 text-sm leading-relaxed">
                  By using Monk Algo's services, you acknowledge that you have read and understood this Risk Disclosure, and you accept full responsibility for your trading decisions and any resulting profits or losses.
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
